/**
 * Form validation, as pure functions.
 *
 * A form is a place a person types, so every helper here answers a question
 * about TEXT, never about a value the program produced. Two rules follow:
 *
 *  - Nothing here throws. A validator that throws on empty input takes the
 *    screen down instead of showing a message, and the crash is the loudest
 *    possible way to be wrong about a blank field.
 *  - Nothing here touches money, dates-as-instants, or the database. The rules
 *    that DO are in `core/` and `domain/`, and are imported rather than
 *    reimplemented — a second rule for "is this a valid date" is a second
 *    answer to the same question.
 */
import { isValidIsoDate, parseIsoDate } from '@domain/financialYear';

/** A field's verdict, and what to tell the user about it. */
export interface FieldValidity {
  readonly ok: boolean;
  readonly message?: string;
}

const OK: FieldValidity = { ok: true };

function fail(message: string): FieldValidity {
  return { ok: false, message };
}

/** A required text field. Whitespace is not content. */
export function required(value: string, label: string): FieldValidity {
  return value.trim() === '' ? fail(`${label} is required.`) : OK;
}

/**
 * A value that must look like money to a human.
 *
 * Deliberately NOT `parseMoney`. That throws on `"1.005"` and on `"abc"`, which
 * is right for a service and wrong for a keystroke handler: a field mid-typing
 * is a normal state, not an error. This accepts what a person is still typing
 * and leaves the throwing parse to the moment of submission.
 *
 * NEGATIVES ARE REFUSED, and that is a domain decision rather than a formatting
 * one. A rate, a quantity or a line total in a sales document is not negative;
 * a document that needs a negative is a credit note, which is a different
 * document type with its own series. Accepting a negative here would let a
 * shopkeeper type a rate that produces a document the engine flags.
 */
export function looksLikeAmount(value: string, label: string): FieldValidity {
  const text = value.trim();
  if (text === '') return fail(`${label} is required.`);
  if (text.startsWith('-')) return fail(`${label} cannot be negative.`);
  if (!/^\d*\.?\d*$/.test(text)) return fail(`${label} must be a number.`);
  const decimals = text.includes('.') ? (text.split('.')[1]?.length ?? 0) : 0;
  if (decimals > 2) return fail(`${label} cannot have more than 2 decimal places.`);
  return OK;
}

/** A quantity, where the unit decides how many decimals are allowed. */
export function looksLikeQuantity(value: string, label: string, precision = 3): FieldValidity {
  const text = value.trim();
  if (text === '') return fail(`${label} is required.`);
  if (text.startsWith('-')) return fail(`${label} cannot be negative.`);
  if (!/^\d*\.?\d*$/.test(text)) return fail(`${label} must be a number.`);
  const decimals = text.includes('.') ? (text.split('.')[1]?.length ?? 0) : 0;
  if (decimals > precision) {
    return fail(`${label} cannot have more than ${precision} decimal place${precision === 1 ? '' : 's'}.`);
  }
  return OK;
}

/**
 * A date field.
 *
 * Delegates the shape check to `domain/financialYear` rather than testing a
 * regex here, because that is where `parseIsoDate` — the function the rest of
 * the app uses to read a date — decides what a valid date is. A local regex
 * would agree with it until it didn't.
 */
export function validDate(value: string, label: string): FieldValidity {
  if (value.trim() === '') return fail(`${label} is required.`);
  return isValidIsoDate(value.trim()) ? OK : fail(`${label} must be a valid date.`);
}

/** A date field that must not be in the future, for a back-dated document. */
export function notInFuture(value: string, label: string, today: string): FieldValidity {
  const base = validDate(value, label);
  if (!base.ok) return base;
  if (!isValidIsoDate(today)) return OK; // No usable today; do not invent a failure.
  return value.trim() > today ? fail(`${label} cannot be in the future.`) : OK;
}

/** The first problem in a list, or null — so one message shows at a time. */
export function firstProblem(results: readonly FieldValidity[]): string | null {
  for (const r of results) {
    if (!r.ok && r.message) return r.message;
  }
  return null;
}

/** Every problem, keyed by field name, for a form that shows them all. */
export function collectProblems(
  entries: Readonly<Record<string, FieldValidity>>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, result] of Object.entries(entries)) {
    if (!result.ok && result.message) out[field] = result.message;
  }
  return out;
}

/** Integer days between two ISO dates, by counting in the proleptic calendar. */
export function daysBetween(from: string, to: string): number | null {
  const a = parseIsoDate(from);
  const b = parseIsoDate(to);
  if (!a || !b) return null;
  return toEpochDay(b) - toEpochDay(a);
}

/**
 * Days since 1970-01-01 for a date, using integer arithmetic only.
 *
 * No `Date`, and no milliseconds. A `Date` is a UTC instant: `new Date('2026-04-01')`
 * is 31 March in some zones, which would file a 1 April invoice into March — the
 * exact class of bug this project's date rules exist to prevent.
 */
const CUMULATIVE_DAYS = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];

function toEpochDay(parts: { year: number; month: number; day: number }): number {
  const { year, month, day } = parts;
  // `parseIsoDate` has already bounded the month to 1-12, but an index that
  // returns `undefined` under `noUncheckedIndexedAccess` would make the sum NaN
  // and every comparison with it silently false. The fallback keeps a bad date
  // producing a wrong NUMBER, which `daysBetween` can report, rather than NaN.
  const beforeMonth = CUMULATIVE_DAYS[month - 1] ?? 0;
  const leap = isLeapYear(year) && month > 2 ? 1 : 0;
  return daysInYearsBefore(year) + beforeMonth + leap + day - 1;
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** Days in complete years before `year`, since year 0. */
function daysInYearsBefore(year: number): number {
  const y = year - 1;
  return y * 365 + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400);
}
